import { StayFlexibleCodeEnum } from '@koob/enums';

/**
 * KD-3496 — early check-in and late check-out are sold as optional supplements,
 * but they are not pure surcharges: the room has to be free outside the booked
 * window. No supplier integration reserves that night for us, so someone
 * reserves it by hand and the UI has to say which night, per room, before the
 * booking is confirmed and again once it exists.
 *
 * Mirrors modules/booking/core/stayFlexible/stayFlexibleRequirements.ts on the
 * API — the API stays the source of truth once a booking is created; these
 * helpers only cover the moment before it is, when there is nothing to read yet.
 */

const MS_PER_DAY = 86400000;

const toIsoDay = (value) =>
  value instanceof Date
    ? value.toISOString().slice(0, 10)
    : String(value).slice(0, 10);

const shiftDays = (isoDay, days) =>
  new Date(Date.parse(`${isoDay}T00:00:00.000Z`) + days * MS_PER_DAY)
    .toISOString()
    .slice(0, 10);

/**
 * The night that has to be free for a service, named by the day it starts on.
 *
 * A 10/08 -> 15/08 stay covers the nights of the 10th to the 14th. An early
 * check-in occupies the room the morning of the 10th, so the night of the 9th is
 * needed; a late check-out keeps it through the 15th, so the night of the 15th —
 * the check-out day itself, the first night outside the stay — is needed.
 */
export const requiredNightFor = (service, { startAt, endAt }) =>
  service === StayFlexibleCodeEnum.EARLY_CHECK_IN
    ? shiftDays(toIsoDay(startAt), -1)
    : toIsoDay(endAt);

export const isStayFlexibleCode = (code) =>
  Object.values(StayFlexibleCodeEnum).includes(code);

/**
 * The hours each service covers.
 *
 * Derived from the service, never stored nor configured: an early check-in
 * occupies the room from midnight to the hotel's check-in hour, a late check-out
 * holds it from the check-out hour to the courtesy limit — the English-hotel
 * convention, check-in at 14:00 and check-out at 12:00. Mirrors
 * STAY_FLEXIBLE_WINDOWS on the API.
 */
export const STAY_FLEXIBLE_WINDOWS = {
  [StayFlexibleCodeEnum.EARLY_CHECK_IN]: {
    startTime: '00:00',
    endTime: '14:00',
  },
  [StayFlexibleCodeEnum.LATE_CHECK_OUT]: {
    startTime: '12:00',
    endTime: '18:00',
  },
};

/** The hours a service covers. */
export const resolveStayFlexibleWindow = (service) =>
  STAY_FLEXIBLE_WINDOWS[service] ?? {};

/**
 * The extra nights implied by what the user picked, one entry per (room,
 * service).
 *
 * `selectedRooms` is the booking tunnel's per-room selection; a room may carry
 * both services (two nights, one either side) and rooms are independent, so a
 * single booking-wide flag would be wrong. Rooms with no stay-flexible
 * supplement contribute nothing, which makes an empty result mean "nothing to
 * warn about".
 */
export const getStayFlexibleRequirements = (selectedRooms, stay) => {
  if (!stay?.startAt || !stay?.endAt) {
    return [];
  }

  return (selectedRooms ?? []).flatMap((room, roomIndex) => {
    // The first promo wins per service, and it is the one whose window and rate
    // are quoted — a cumulative pair would otherwise name the same night twice.
    const services = new Map();
    (room?.optionalPromos ?? []).forEach((promo) => {
      if (isStayFlexibleCode(promo?.code) && !services.has(promo.code)) {
        services.set(promo.code, promo);
      }
    });

    return [...services].map(([service, promo]) => {
      const benefitValue = Number(promo?.benefitValue);

      return {
        roomIndex,
        service,
        requiredDate: requiredNightFor(service, stay),
        ...resolveStayFlexibleWindow(service),
        benefitKind: promo?.kind,
        benefitValue: Number.isFinite(benefitValue) ? benefitValue : undefined,
      };
    });
  });
};
